import React, { useState, useEffect } from 'react';
import { Search, Filter, Grid, List, BookOpen, Target, Star } from 'lucide-react';
import learningScheduler from '../services/learningScheduler';
import { getTitleByPreference } from '../utils/chineseCharacterUtils';

const VocabularyBrowser = ({ 
  onSetSelect, 
  userSettings,
  className = "" 
}) => {
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [showStudyModeOnly, setShowStudyModeOnly] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [allSets, setAllSets] = useState([]);
  const [organizedSets, setOrganizedSets] = useState({});
  const [categories, setCategories] = useState([]);
  const [studyModeSets, setStudyModeSets] = useState([]);

  // Load data from Supabase
  useEffect(() => {
    const loadData = async () => {
      try {
        // Ensure sets are loaded
        await learningScheduler.ensureLoaded();
        
        // Get all data
        const sets = learningScheduler.allSets;
        const organized = learningScheduler.getAllSetsOrganized();
        const cats = Object.keys(organized).sort((a, b) => a.localeCompare(b)); // Sort categories alphabetically
        const studyMode = learningScheduler.getStudyModeSets();
        
        setAllSets(sets);
        setOrganizedSets(organized);
        setCategories(cats);
        setStudyModeSets(studyMode);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading vocabulary browser data:', error);
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Filter sets based on search and filters
  const getFilteredSets = () => {
    let filtered = allSets;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(set => {
        const title = getTitleByPreference(set, userSettings.chineseCharacterPreference);
        return title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          set.titleEnglish.toLowerCase().includes(searchTerm.toLowerCase()) ||
          set.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
          set.categoryEnglish.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(set => 
        set.category === selectedCategory || set.categoryEnglish === selectedCategory
      );
    }

    // Difficulty filter
    if (selectedDifficulty !== 'all') {
      filtered = learningScheduler.getSetsByDifficulty(selectedDifficulty);
      filtered = filtered.filter(set => 
        filtered.some(fSet => fSet.id === set.id)
      );
    }

    // Study mode filter
    if (showStudyModeOnly) {
      filtered = filtered.filter(set => set.studyMode || set.testMode);
    }

    return filtered;
  };

  const filteredSets = getFilteredSets();

  // Get difficulty for a set
  const getSetDifficulty = (set) => {
    return learningScheduler.getAverageFrequency(set);
  };

  // Get difficulty color
  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'high': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Get difficulty label
  const getDifficultyLabel = (difficulty) => {
    switch (difficulty) {
      case 'high': return 'Easy';
      case 'medium': return 'Medium';
      case 'low': return 'Hard';
      default: return 'Unknown';
    }
  };

  // Get completion status for a set
  const getSetStatus = (setId) => {
    const calendarData = userSettings.progress.calendarData || {};
    const completedDates = Object.keys(calendarData).filter(date => {
      const data = calendarData[date];
      return data.setId === setId && (data.status === 'DS' || data.status === 'DF');
    });
    
    if (completedDates.length === 0) return 'not-started';
    const lastCompletion = completedDates.sort().pop();
    const lastData = calendarData[lastCompletion];
    return lastData.status === 'DS' ? 'completed' : 'failed';
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'border-green-500 bg-green-50';
      case 'failed': return 'border-red-500 bg-red-50';
      case 'not-started': return 'border-gray-300 bg-white';
      default: return 'border-gray-300 bg-white';
    }
  };

  if (isLoading) {
    return (
      <div className={`bg-white rounded-xl shadow-sm ${className} p-6`}>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading vocabulary sets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm ${className}`}>
      {/* Header */}
      <div className="p-6 border-b">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-800">Vocabulary Browser</h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search vocabulary sets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {category} ({organizedSets[category].length})
                </option>
              ))}
            </select>

            {/* Difficulty Filter */}
            <select
              value={selectedDifficulty}
              onChange={(e) => setSelectedDifficulty(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Difficulties</option>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>

            {/* Study Mode Filter */}
            <label className="flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                checked={showStudyModeOnly}
                onChange={(e) => setShowStudyModeOnly(e.target.checked)}
                className="mr-2"
              />
              Study Mode Only
            </label>
          </div>

          {/* Results Count */}
          <div className="text-sm text-gray-600">
            Showing {filteredSets.length} of {allSets.length} sets
          </div>
        </div>
      </div>

      {/* Sets Display */}
      <div className="p-6">
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredSets.map((set, index) => {
              const difficulty = getSetDifficulty(set);
              const status = getSetStatus(set.id);
              const title = getTitleByPreference(set, userSettings.chineseCharacterPreference);
              
              return (
                <div
                  key={set.id}
                  onClick={() => onSetSelect && onSetSelect(set, set.originalIndex)}
                  className={`
                    p-4 border-2 rounded-xl cursor-pointer transition-all hover:shadow-md
                    ${getStatusColor(status)}
                  `}
                >
                  <div className="flex items-start justify-between mb-3">
                    <img 
                      src={set.imageUrl} 
                      alt={title}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                    <div className="flex items-center space-x-1">
                      {(set.studyMode || set.testMode) && (
                        <BookOpen className="w-4 h-4 text-blue-500" />
                      )}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(difficulty)}`}>
                        {getDifficultyLabel(difficulty)}
                      </span>
                    </div>
                  </div>
                  
                  <h3 className="font-semibold text-gray-800 mb-1">{title}</h3>
                  <p className="text-sm text-gray-600 mb-2">{set.titleEnglish}</p>
                  <p className="text-xs text-gray-500">{set.words.length} words</p>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredSets.map((set, index) => {
              const difficulty = getSetDifficulty(set);
              const status = getSetStatus(set.id);
              const title = getTitleByPreference(set, userSettings.chineseCharacterPreference);
              
              return (
                <div
                  key={set.id}
                  onClick={() => onSetSelect && onSetSelect(set, set.originalIndex)}
                  className={`
                    p-4 border-2 rounded-lg cursor-pointer transition-all hover:shadow-sm
                    ${getStatusColor(status)}
                  `}
                >
                  <div className="flex items-center space-x-4">
                    <img 
                      src={set.imageUrl} 
                      alt={title}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                    
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-semibold text-gray-800">{title}</h3>
                        {(set.studyMode || set.testMode) && (
                          <BookOpen className="w-4 h-4 text-blue-500" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-1">{set.titleEnglish}</p>
                      <p className="text-xs text-gray-500">{set.words.length} words</p>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(difficulty)}`}>
                        {getDifficultyLabel(difficulty)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {filteredSets.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Search className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No vocabulary sets found matching your criteria.</p>
            <p className="text-sm">Try adjusting your search or filters.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VocabularyBrowser;
